import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ChipProceso } from '@intelishs-group/ui';
import { CalculosEnMarchaService } from '@services/calculos-en-marcha.service';

/**
 * Las pastillas de los cálculos en marcha (2026-10-15): una por cálculo que el usuario lanzó, en la
 * barra del marco. Mientras calcula, el anillo dice cuánto lleva; al terminar o detenerse lleva su ×
 * para dejar de avisarse sin abrirlo. El clic lleva a «Calcular» con su nómina.
 *
 * Desde el 2026-10-23 la pastilla es `<intelishs-chip-proceso>` de `@intelishs-group/ui` (≥ 5.3.3,
 * que sumó `[porcentaje]` y `[quitable]` justo para este caso): aquí sólo se cablea
 * `CalculosEnMarchaService` a ella. Antes era una pieza propia, con su anillo y su CSS.
 *
 * El host es `display: contents` (lo mismo que `tanda-header` en ffacturacion): cada pastilla queda
 * hija directa de la fila de la barra, que pone el centrado y la separación; sin cálculos no ocupa
 * lugar. Y es la región `status`: vive siempre, aunque esté vacía, para que el lector de pantalla
 * anuncie la primera pastilla que llegue.
 */
@Component({
  selector: 'app-calculos-barra',
  imports: [ChipProceso],
  template: `
    @for (a of marcha.avisos(); track a.id) {
      <intelishs-chip-proceso
        [tono]="a.tono"
        [texto]="a.texto"
        [titulo]="a.accion ? a.texto + '. ' + a.accion : a.texto"
        [porcentaje]="a.tono === 'progreso' ? (a.porcentaje ?? null) : null"
        [quitable]="!!a.quitable"
        (abrir)="abrir(a.id)"
        (quitar)="quitar(a.id)"
      />
    }
  `,
  styles: `
    :host {
      display: contents;
    }
  `,
  host: { role: 'status', 'aria-live': 'polite' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalculosBarra {
  private readonly router = inject(Router);
  protected readonly marcha = inject(CalculosEnMarchaService);

  /** Lleva a «Calcular» con su nómina; si ya terminó, deja de avisarse. */
  protected abrir(id: string): void {
    const corrida = Number(id);
    const nomina = this.marcha.nominaDe(corrida);
    if (this.marcha.terminado(corrida)) this.marcha.quitar(corrida);
    if (nomina !== null) void this.router.navigate(['/nomina/calcular'], { queryParams: { nomina } });
  }

  /** La × ya terminó su salida: deja de avisarse (el cálculo sigue si estaba en marcha). */
  protected quitar(id: string): void {
    this.marcha.quitar(Number(id));
  }
}
